import React from 'react';
import { Camera, CreditCard, Trash2, X } from 'lucide-react';

interface PhotosDocumentsSectionProps {
  formData: {
    photos: string[];
    visitingCardImage: string;
    wtfStickerImage: string;
  };
  editMode: boolean;
  existingOutlet?: any;
  onStartCamera: () => void;
  onStartWTFStickerScanner: () => void;
  onStartVisitingCardScanner: () => void;
  onRemovePhoto: (index: number) => void;
  onClearVisitingCard: () => void;
  onClearWTFSticker: () => void;
}

export const PhotosDocumentsSection: React.FC<PhotosDocumentsSectionProps> = ({
  formData,
  editMode,
  existingOutlet,
  onStartCamera,
  onStartWTFStickerScanner,
  onStartVisitingCardScanner,
  onRemovePhoto,
  onClearVisitingCard,
  onClearWTFSticker
}) => {
  const createImageViewer = (imageSrc: string, altText: string) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4';
    modal.innerHTML = `
      <div class="relative max-w-4xl max-h-full">
        <img src="${imageSrc}" alt="${altText}" class="max-w-full max-h-full object-contain" />
        <button class="absolute top-2 right-2 p-2 bg-white rounded-full hover:bg-gray-200 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('button')?.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Photos & Documents</h3>
      
      <div className="space-y-4">
        {/* Existing Photos Display (Edit Mode Only) */}
        {editMode && existingOutlet && (existingOutlet.photos?.length > 0 || existingOutlet.visitingCardImage) && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
              <Camera className="w-4 h-4" />
              <span>Existing Photos</span>
            </h4>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Regular Photos */}
              {existingOutlet.photos && existingOutlet.photos.map((photo: string, index: number) => (
                <div key={index} className="relative group">
                  <img
                    src={photo}
                    alt={`Outlet Photo ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border border-gray-300 cursor-pointer"
                    onClick={() => createImageViewer(photo, `Outlet Photo ${index + 1} Full Size`)}
                  />
                  <div className="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                    Photo {index + 1}
                  </div>
                </div>
              ))}
              
              {/* Visiting Card */}
              {existingOutlet.visitingCardImage && (
                <div className="relative group">
                  <img
                    src={existingOutlet.visitingCardImage}
                    alt="Visiting Card"
                    className="w-full h-24 object-contain bg-gray-50 rounded-lg border border-gray-300 cursor-pointer"
                    onClick={() => createImageViewer(existingOutlet.visitingCardImage, 'Visiting Card Full Size')}
                  />
                  <div className="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                    Visiting Card
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Outlet Photos Section */}
        <div>
          <button
            onClick={onStartCamera}
            className="w-full flex items-center justify-center space-x-2 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors mb-3"
          >
            <Camera className="w-5 h-5" />
            <span>Take Photo</span>
          </button>

          {/* Take WTF Contact Sticker Photo Button */}
          <button
            onClick={onStartWTFStickerScanner}
            className="w-full flex items-center justify-center space-x-2 bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <CreditCard className="w-5 h-5" />
            <span>Take WTF Contact Sticker Photo</span>
          </button>
          
          {formData.photos.length > 0 && (
            <div className="mt-3 space-y-3">
              {formData.photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img
                    src={photo}
                    alt={`Outlet Photo ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={() => onRemovePhoto(index)}
                    className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                    Photo {index + 1} of {formData.photos.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Visiting Card Section */}
        {formData.visitingCardImage && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <CreditCard className="w-4 h-4" />
                <span>Visiting Card</span>
              </h4>
              <button
                onClick={onStartVisitingCardScanner}
                className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
              >
                Rescan
              </button>
            </div>
            
            <div className="relative group">
              <img
                src={formData.visitingCardImage}
                alt="Visiting Card"
                className="w-full h-32 object-contain bg-gray-50 rounded-lg border border-gray-300"
              />
              
              {/* Overlay buttons on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-3">
                <button
                  onClick={() => createImageViewer(formData.visitingCardImage, 'Visiting Card Full Size')}
                  className="p-2 bg-white rounded-full hover:bg-gray-200 transition-colors"
                  title="View Full Size"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
                
                <button
                  onClick={onClearVisitingCard}
                  className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  title="Remove Visiting Card"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                Visiting Card
              </div>
            </div>
          </div>
        )}

        {/* WTF Contact Sticker Section */}
        {formData.wtfStickerImage && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <span className="text-orange-600">🏷️</span>
                <span>WTF Contact Sticker</span>
              </h4>
              <button
                onClick={onStartWTFStickerScanner}
                className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
              >
                Rescan
              </button>
            </div>
            
            <div className="relative group">
              <img
                src={formData.wtfStickerImage}
                alt="WTF Contact Sticker"
                className="w-full h-32 object-contain bg-gray-50 rounded-lg border border-gray-300"
              />
              
              {/* Overlay buttons on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-3">
                <button
                  onClick={() => createImageViewer(formData.wtfStickerImage, 'WTF Contact Sticker Full Size')}
                  className="p-2 bg-white rounded-full hover:bg-gray-200 transition-colors"
                  title="View Full Size"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
                
                <button
                  onClick={onClearWTFSticker}
                  className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  title="Remove WTF Contact Sticker"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                WTF Contact Sticker
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotosDocumentsSection;
